/**
 * Collection Page - Dynamic Shopify AJAX Filtering, Sorting & Sub-collections
 * Matches Redesign/collection.html layout and behavior
 *
 * SOLD-OUT LAST: uses Shopify's Availability filter (filter.v.availability)
 * internally. In-stock pages load first, then sold-out pages load after them.
 * Falls back to the normal behaviour if the Availability filter is not enabled
 * in Search & Discovery (data-avail-filter="false" on #grid).
 */
(() => {
  const AVAIL_PARAM = 'filter.v.availability'; // 1 = in stock, 0 = sold out

  const SELECTORS = {
    grid: '#grid',
    toolbar: '#toolbar',
    sort: '#sort',
    filterBtn: '.filter-btn',
    filters: '#filters',
    filterBody: '#filters-body',
    activeFilters: '#active-filters',
    filterCount: '#filter-count',
    filtersActive: '#filters-active',
    resultCount: '#result-count',
    loadMore: '#load-more',
    more: '#more',
    moreCount: '#more-count',
    moreBar: '#more-bar',
    plpEmpty: '#plp-empty',
    clearAll: '[id^="clear-all"]',
    cats: '.cats',
    cat: '.cat',
    plpHero: '.plp-hero',
    plpHeroCopy: '.plp-hero-copy',
    plpHeroImg: '.plp-hero-img',
    plpTitle: '#plp-title',
    plpFacts: '.plp-facts',
    plpAbout: '.plp-about',
    crumbs: '.crumbs',
    scrim: '#scrim'
  };

  const STATE = {
    collectionHandle: '',
    collectionId: '',
    baseUrl: '',
    params: new URLSearchParams(),
    page: 1,
    perPage: 24,
    loading: false,
    pendingFilters: false,
    fetchId: 0,
    enableAjax: true,
    // sold-out-last state
    availEnabled: false,
    phase: 'in', // 'in' = in-stock pages, 'out' = sold-out pages
    inTotal: 0,
    outTotal: 0
  };
  let filterDebounce = null;

  // Infinite scroll variables
  let infiniteScrollObserver = null;

  const CACHE = new Map();

  function observeInfiniteScroll() {
    // Disconnect existing observer if any
    if (infiniteScrollObserver) {
      infiniteScrollObserver.disconnect();
    }

    // Create sentinel element at end of grid for observation
    const gridEl = document.querySelector(SELECTORS.grid);
    if (!gridEl) return;

    // Remove existing sentinel if any
    const existingSentinel = document.querySelector('#infinite-scroll-sentinel');
    if (existingSentinel) {
      existingSentinel.remove();
    }

    // Add sentinel element
    const sentinel = document.createElement('div');
    sentinel.id = 'infinite-scroll-sentinel';
    sentinel.style.height = '1px';
    gridEl.appendChild(sentinel);

    // Set up observer
    infiniteScrollObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !STATE.loading && hasMoreOverall()) {
          handleLoadMore(true);
        }
      });
    }, { rootMargin: '200px' });

    infiniteScrollObserver.observe(sentinel);
  }

  function init() {
    const plpPage = document.querySelector('.plp-page');
    if (!plpPage) return;

    const gridEl = document.querySelector(SELECTORS.grid);

    STATE.collectionHandle = plpPage.dataset.collectionHandle;
    STATE.collectionId = plpPage.dataset.collectionId;
    STATE.baseUrl = window.location.pathname;
    STATE.perPage = parseInt(plpPage.dataset.perPage || '24', 10);
    STATE.enableAjax = window.enableAjax !== false;
    STATE.infiniteScroll = plpPage.dataset.infiniteScroll === 'true';
    STATE.availEnabled = !!gridEl && gridEl.dataset.availFilter === 'true';

    parseUrlParams();
    bindEvents();
    bindDrawerEvents();
    hideAvailabilityFilter(document.querySelector(SELECTORS.filterBody));

    // Re-fetch page 1 so the order is "in stock first". Skipped when the
    // Availability filter is off and we are already on page 1.
    const startPage = parseInt(gridEl?.dataset.page || '1', 10);
    if (STATE.availEnabled || startPage !== 1) {
      fetchAndRenderProducts({ reset: true, pushState: false, initial: true });
    } else {
      STATE.inTotal = parseInt(gridEl?.dataset.totalProducts || '0', 10);
      STATE.outTotal = 0;
    }

    // Infinite scroll: auto-trigger Load More when user scrolls near bottom
    if (STATE.infiniteScroll) {
      observeInfiniteScroll();
    }
  }


  function parseUrlParams() {
    const url = new URL(window.location.href);
    STATE.params = new URLSearchParams(url.search);
    // page and availability are controlled internally
    STATE.params.delete('page');
    STATE.params.delete(AVAIL_PARAM);
    STATE.page = 1;

    if (STATE.params.has('sort_by')) {
      const sortSelect = document.querySelector(SELECTORS.sort);
      if (sortSelect) sortSelect.value = STATE.params.get('sort_by');
    }
  }

  function bindEvents() {
    // Sorting
    const sortEl = document.querySelector(SELECTORS.sort);
    sortEl?.addEventListener('change', handleSortChange);

    // Load More (Pagination)
    const loadMoreEl = document.querySelector(SELECTORS.loadMore);
    loadMoreEl?.addEventListener('click', () => handleLoadMore(false));

    // Clear All Filters
    document.addEventListener('click', e => {
      if (e.target.closest(SELECTORS.clearAll)) {
        e.preventDefault();
        handleClearAll();
      }
    });

    // Sub-collection clicking
    document.querySelector(SELECTORS.cats)?.addEventListener('click', handleSubCollectionClick);

    document.addEventListener('change', e => {
      if (e.target.closest(SELECTORS.filterBody) || e.target.closest(SELECTORS.filters)) {
        scheduleFilterChange();
      }
    });

    // Show Results button in drawer foot
    document.addEventListener('click', e => {
      if (e.target.closest('#show-results')) {
        e.preventDefault();
        closeFilterDrawer();
        scrollToToolbar();
      }
    });

    // Active filter pill click (remove filter)
    document.addEventListener('click', e => {
      const pill = e.target.closest('[data-rm]');
      if (pill && pill.closest(SELECTORS.activeFilters)) {
        e.preventDefault();
        handleActiveFilterRemove(pill);
      }
    });

    // Browser navigation (Back / Forward)
    window.addEventListener('popstate', handlePopState);
  }

  function bindDrawerEvents() {
    const filtersDrawer = document.querySelector(SELECTORS.filters);
    const scrim = document.querySelector(SELECTORS.scrim);
    if (!filtersDrawer) return;

    // Open filter drawer
    document.querySelector(SELECTORS.filterBtn)?.addEventListener('click', e => {
      e.preventDefault();
      openFilterDrawer();
    });

    // Close buttons inside drawer
    filtersDrawer.querySelectorAll('[data-close]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        closeFilterDrawer();
      });
    });

    // Scrim click closes drawer
    scrim?.addEventListener('click', () => {
      if (filtersDrawer.classList.contains('is-open')) {
        closeFilterDrawer();
      }
    });

    // Escape key closes drawer
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && filtersDrawer.classList.contains('is-open')) {
        closeFilterDrawer();
      }
    });
  }

  function openFilterDrawer() {
    const filtersDrawer = document.querySelector(SELECTORS.filters);
    const scrim = document.querySelector(SELECTORS.scrim);
    const filterBtn = document.querySelector(SELECTORS.filterBtn);

    filtersDrawer?.classList.add('is-open');
    scrim?.classList.add('is-on');
    document.documentElement.classList.add('is-locked');
    filterBtn?.setAttribute('aria-expanded', 'true');
  }

  function closeFilterDrawer() {
    const filtersDrawer = document.querySelector(SELECTORS.filters);
    const scrim = document.querySelector(SELECTORS.scrim);
    const filterBtn = document.querySelector(SELECTORS.filterBtn);

    filtersDrawer?.classList.remove('is-open');
    scrim?.classList.remove('is-on');
    document.documentElement.classList.remove('is-locked');
    filterBtn?.setAttribute('aria-expanded', 'false');
  }

  async function handleSortChange(e) {
    if (STATE.loading) return;
    STATE.params.set('sort', e.target.value);
    STATE.params.set('sort_by', e.target.value);
    STATE.params.delete('page');
    STATE.page = 1;
    await fetchAndRenderProducts({ reset: true, pushState: true });
  }

  async function handleLoadMore(isInfiniteScroll) {
    if (STATE.loading) return;

    const prev = { page: STATE.page, phase: STATE.phase };

    if (hasMoreInPhase()) {
      STATE.page += 1;
    } else if (STATE.availEnabled && STATE.phase === 'in' && STATE.outTotal > 0) {
      // in-stock finished -> start loading sold-out products
      STATE.phase = 'out';
      STATE.page = 1;
    } else {
      return;
    }

    // Record count of current cards before load so we can scroll to first new one
    const gridEl = document.querySelector(SELECTORS.grid);
    const prevCount = gridEl ? gridEl.querySelectorAll('.card').length : 0;

    const ok = await fetchAndRenderProducts({ reset: false, pushState: false });
    if (ok === false) {
      // request failed: restore so the next click retries the same page
      STATE.page = prev.page;
      STATE.phase = prev.phase;
      return;
    }

    // Scroll to first newly appended card
    if (gridEl && !isInfiniteScroll) {
      const allCards = gridEl.querySelectorAll('.card');
      const firstNew = allCards[prevCount];
      if (firstNew) {
        const headerH = document.querySelector('.header')?.offsetHeight || 88;
        const top = firstNew.getBoundingClientRect().top + window.scrollY - headerH - 16;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  }


  async function handleClearAll() {
    if (STATE.loading) return;
    const sortVal = STATE.params.get('sort_by') || STATE.params.get('sort');
    STATE.params = new URLSearchParams();
    if (sortVal) {
      STATE.params.set('sort_by', sortVal);
      STATE.params.set('sort', sortVal);
    }
    STATE.page = 1;

    // Uncheck all inputs
    document.querySelectorAll(`${SELECTORS.filterBody} input`).forEach(i => { i.checked = false; });
    await fetchAndRenderProducts({ reset: true, pushState: true });
  }

  async function handleSubCollectionClick(e) {
    const cat = e.target.closest(SELECTORS.cat);
    if (!cat || STATE.loading) return;

    const handle = cat.dataset.collectionHandle;
    const targetUrl = cat.dataset.collectionUrl || (handle ? `/collections/${handle}` : null);
    if (!targetUrl) return;

    document.querySelectorAll(SELECTORS.cat).forEach(c => c.setAttribute('aria-pressed', 'false'));
    cat.setAttribute('aria-pressed', 'true');

    STATE.baseUrl = targetUrl.split('?')[0];
    STATE.collectionHandle = handle;
    const sortVal = STATE.params.get('sort_by') || STATE.params.get('sort');
    STATE.params = new URLSearchParams();
    if (sortVal) {
      STATE.params.set('sort_by', sortVal);
      STATE.params.set('sort', sortVal);
    }
    STATE.page = 1;

    await fetchAndRenderProducts({ reset: true, pushState: true, updateHero: true });
    scrollToToolbar();
  }

  function collectFilterParams() {
    const formParams = new URLSearchParams();
    const sortVal = document.querySelector(SELECTORS.sort)?.value || STATE.params.get('sort_by');
    if (sortVal && sortVal !== 'featured') {
      formParams.set('sort_by', sortVal);
    }

    document.querySelectorAll(`${SELECTORS.filterBody} input[type="checkbox"]:checked`).forEach(inp => {
      const name = inp.name || inp.dataset.filterParam;
      if (name === AVAIL_PARAM) return;
      if (name && inp.value) {
        formParams.append(name, inp.value);
      }
    });

    document.querySelectorAll(`${SELECTORS.filterBody} input[type="number"]`).forEach(inp => {
      const name = inp.name || inp.dataset.filterParam;
      if (name && inp.value && inp.value.trim() !== '') {
        formParams.set(name, inp.value.trim());
      }
    });

    return formParams;
  }

  function scheduleFilterChange() {
    STATE.params = collectFilterParams();
    STATE.page = 1;
    STATE.params.delete('page');
    STATE.pendingFilters = true;

    clearTimeout(filterDebounce);
    filterDebounce = setTimeout(() => {
      handleFilterChange();
    }, 280);
  }

  async function handleFilterChange() {
    STATE.params = collectFilterParams();
    STATE.page = 1;
    STATE.params.delete('page');

    if (STATE.loading) {
      STATE.pendingFilters = true;
      return;
    }

    const showBtn = document.getElementById('show-results');
    if (showBtn) showBtn.textContent = 'Updating...';

    STATE.pendingFilters = false;
    await fetchAndRenderProducts({ reset: true, pushState: true });

    if (showBtn) showBtn.textContent = 'Show results';

    if (STATE.pendingFilters) {
      STATE.pendingFilters = false;
      await handleFilterChange();
    }
  }

  async function handleActiveFilterRemove(btn) {
    if (STATE.loading) return;
    const paramName = btn.dataset.rm;
    const paramVal = btn.dataset.v;

    if (paramVal === 'price') {
      STATE.params.delete(`${paramName}.gte`);
      STATE.params.delete(`${paramName}.lte`);
    } else {
      const existing = STATE.params.getAll(paramName);
      STATE.params.delete(paramName);
      existing.filter(v => v !== paramVal).forEach(v => {
        STATE.params.append(paramName, v);
      });
    }

    STATE.params.delete('page');
    STATE.page = 1;
    await fetchAndRenderProducts({ reset: true, pushState: true });
  }

  function handlePopState() {
    STATE.baseUrl = window.location.pathname;
    parseUrlParams();
    fetchAndRenderProducts({ reset: true, pushState: false, updateHero: true });
  }

  /* ---------- Sold-out-last helpers ---------- */

  function buildFetchUrl(phase = STATE.phase, page = STATE.page) {
    const url = new URL(STATE.baseUrl, window.location.origin);
    STATE.params.forEach((value, key) => {
      if (key === 'page' || key === AVAIL_PARAM) return;
      url.searchParams.append(key, value);
    });
    if (STATE.availEnabled) {
      url.searchParams.set(AVAIL_PARAM, phase === 'in' ? '1' : '0');
    }
    url.searchParams.set('page', page);
    return url.toString();
  }

  async function getHtml(fetchUrl) {
    if (CACHE.has(fetchUrl)) return CACHE.get(fetchUrl);
    const response = await fetch(fetchUrl, {
      headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = await response.text();
    if (CACHE.size > 25) CACHE.clear();
    CACHE.set(fetchUrl, html);
    return html;
  }

  function parseDoc(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function gridTotal(doc) {
    return parseInt(doc.querySelector(SELECTORS.grid)?.dataset.totalProducts || '0', 10);
  }

  // more pages left inside the current phase (in-stock or sold-out)?
  function hasMoreInPhase() {
    const g = document.querySelector(SELECTORS.grid);
    if (!g) return false;
    return parseInt(g.dataset.page || '1', 10) < parseInt(g.dataset.totalPages || '1', 10);
  }

  // more to load anywhere (rest of this phase, or the sold-out phase)?
  function hasMoreOverall() {
    return hasMoreInPhase() || (STATE.availEnabled && STATE.phase === 'in' && STATE.outTotal > 0);
  }

  function updateMore() {
    const moreEl = document.querySelector(SELECTORS.more);
    if (!moreEl) return;
    const total = STATE.inTotal + STATE.outTotal;
    const shown = document.querySelectorAll(`${SELECTORS.grid} .card`).length;

    moreEl.hidden = total <= STATE.perPage;

    const countEl = document.querySelector(SELECTORS.moreCount);
    if (countEl) countEl.textContent = `Showing 1–${shown} of ${total}`;

    const barEl = document.querySelector(SELECTORS.moreBar);
    if (barEl) barEl.style.width = total ? `${Math.min(100, Math.round(shown * 100 / total))}%` : '0%';

    const btn = document.querySelector(SELECTORS.loadMore);
    if (btn) btn.hidden = !hasMoreOverall();
  }

  // Availability is used internally, so never show it to customers
  function hideAvailabilityFilter(root) {
    if (!root) return;
    root.querySelectorAll('input').forEach(inp => {
      const name = inp.name || inp.dataset.filterParam || '';
      if (name !== AVAIL_PARAM) return;
      const group = inp.closest('details.fgroup') || inp.closest('.fgroup');
      if (group) group.remove();
      else (inp.closest('label') || inp).remove();
    });
  }

  /* ---------- Main fetch + render ---------- */

  async function fetchAndRenderProducts({ reset, pushState, updateHero, initial }) {
    if (STATE.loading) return;
    const fetchId = ++STATE.fetchId;
    STATE.loading = true;
    setLoadingState(true);

    if (reset) {
      STATE.phase = 'in';
      STATE.page = 1;
    }

    let doc;
    try {
      if (reset && STATE.availEnabled) {
        // fetch first in-stock page + first sold-out page (for totals)
        const [inHtml, outHtml] = await Promise.all([
          getHtml(buildFetchUrl('in', 1)),
          getHtml(buildFetchUrl('out', 1)).catch(() => null)
        ]);
        const inDoc = parseDoc(inHtml);
        const outDoc = outHtml ? parseDoc(outHtml) : null;
        STATE.inTotal = gridTotal(inDoc);
        STATE.outTotal = outDoc ? gridTotal(outDoc) : 0;
        doc = inDoc;
        if (STATE.inTotal === 0 && STATE.outTotal > 0) {
          // everything in this view is sold out
          STATE.phase = 'out';
          doc = outDoc;
        }
      } else if (reset) {
        const html = await getHtml(buildFetchUrl('in', 1));
        doc = parseDoc(html);
        STATE.inTotal = gridTotal(doc);
        STATE.outTotal = 0;
      } else {
        const html = await getHtml(buildFetchUrl());
        doc = parseDoc(html);
      }
    } catch (err) {
      console.error('Collection fetch error:', err);
      setLoadingState(false);
      STATE.loading = false;
      return false;
    }

    if (fetchId !== STATE.fetchId) return;

    if (STATE.pendingFilters) {
      setLoadingState(false);
      STATE.loading = false;
      STATE.pendingFilters = false;
      handleFilterChange();
      return;
    }

    // 1. Update Grid
    const newGrid = doc.querySelector(SELECTORS.grid);
    const gridEl = document.querySelector(SELECTORS.grid);
    if (newGrid && gridEl) {
      // promo card only makes sense at the top, not inside sold-out pages
      if (!reset && STATE.phase === 'out') {
        newGrid.querySelectorAll('.promo').forEach(p => p.remove());
      }

      // On first load, if page 1 already has no sold-out cards, the server
      // rendered grid is identical -> avoid a needless re-render.
      const skipGrid = initial &&
        STATE.phase === 'in' &&
        gridEl.dataset.page === '1' &&
        !gridEl.querySelector('.card.is-sold');

      if (!skipGrid) {
        if (reset) {
          gridEl.innerHTML = newGrid.innerHTML;
        } else {
          gridEl.insertAdjacentHTML('beforeend', newGrid.innerHTML);
        }
      }
      gridEl.dataset.page = newGrid.dataset.page;
      gridEl.dataset.totalPages = newGrid.dataset.totalPages;
      gridEl.dataset.totalProducts = newGrid.dataset.totalProducts;
    }

    // 2. Update Result Count (in-stock + sold-out together)
    const newResultCount = doc.querySelector(SELECTORS.resultCount);
    const resultCountEl = document.querySelector(SELECTORS.resultCount);
    if (newResultCount && resultCountEl) {
      resultCountEl.innerHTML = newResultCount.innerHTML;
      if (STATE.availEnabled) {
        const countB = resultCountEl.querySelector('b');
        if (countB) countB.textContent = STATE.inTotal + STATE.outTotal;
      }
    }

    // 3. Update More / Pagination
    updateMore();

    // 4. Update Empty State
    if (reset) {
      const newEmpty = doc.querySelector(SELECTORS.plpEmpty);
      const emptyEl = document.querySelector(SELECTORS.plpEmpty);
      if (newEmpty && emptyEl) {
        emptyEl.hidden = newEmpty.hidden;
      }
    }

    if (reset) {
      // 5. Update Active Filter Pills
      const newActiveFilters = doc.querySelector(SELECTORS.activeFilters);
      const activeFiltersEl = document.querySelector(SELECTORS.activeFilters);
      if (newActiveFilters && activeFiltersEl) {
        activeFiltersEl.hidden = newActiveFilters.hidden;
        activeFiltersEl.innerHTML = newActiveFilters.innerHTML;
      }

      // 6. Update Filter Drawer body
      const newFilterBody = doc.querySelector(SELECTORS.filterBody);
      const filterBodyEl = document.querySelector(SELECTORS.filterBody);
      if (newFilterBody && filterBodyEl) {
        const openGroups = new Set(
          Array.from(filterBodyEl.querySelectorAll('details.fgroup'))
            .filter(d => d.open)
            .map(d => d.getAttribute('data-g'))
        );
        filterBodyEl.innerHTML = newFilterBody.innerHTML;
        filterBodyEl.querySelectorAll('details.fgroup').forEach(d => {
          const key = d.getAttribute('data-g');
          if (openGroups.has(key)) d.open = true;
        });
        hideAvailabilityFilter(filterBodyEl);
        syncFilterInputs(filterBodyEl);
      }

      // 7 & 8. Filter badge counts: counted from the visible filter pills,
      // so the internal availability filter is never included.
      const pillCount = document.querySelectorAll(`${SELECTORS.activeFilters} .pill-f`).length;

      const filterCountEl = document.querySelector(SELECTORS.filterCount);
      if (filterCountEl) {
        filterCountEl.hidden = pillCount === 0;
        filterCountEl.textContent = pillCount;
      }

      const filtersActiveEl = document.querySelector(SELECTORS.filtersActive);
      if (filtersActiveEl) {
        filtersActiveEl.hidden = pillCount === 0;
        filtersActiveEl.textContent = `(${pillCount})`;
      }
    }

    // 9. If sub-collection was changed, update Hero & About sections
    if (updateHero) {
      // Title
      const newTitle = doc.querySelector(SELECTORS.plpTitle);
      const titleEl = document.querySelector(SELECTORS.plpTitle);
      if (newTitle && titleEl) titleEl.textContent = newTitle.textContent;

      // Label
      const newLabel = doc.querySelector(`${SELECTORS.plpHeroCopy} .label`);
      const labelEl = document.querySelector(`${SELECTORS.plpHeroCopy} .label`);
      if (newLabel && labelEl) labelEl.textContent = newLabel.textContent;

      // Description
      const newDesc = doc.querySelector(`${SELECTORS.plpHeroCopy} > p`);
      let descEl = document.querySelector(`${SELECTORS.plpHeroCopy} > p`);
      if (newDesc) {
        if (!descEl) {
          descEl = document.createElement('p');
          document.querySelector(SELECTORS.plpHeroCopy)?.appendChild(descEl);
        }
        descEl.textContent = newDesc.textContent;
      } else if (descEl) {
        descEl.remove();
      }

      // Facts
      const newFacts = doc.querySelector(SELECTORS.plpFacts);
      const factsEl = document.querySelector(SELECTORS.plpFacts);
      if (newFacts && factsEl) factsEl.innerHTML = newFacts.innerHTML;

      // Hero Image
      const newImg = doc.querySelector(SELECTORS.plpHeroImg);
      const imgEl = document.querySelector(SELECTORS.plpHeroImg);
      if (newImg && imgEl) {
        imgEl.innerHTML = newImg.innerHTML;
      } else if (!newImg && imgEl) {
        imgEl.innerHTML = '';
      }

      // About Section
      const newAbout = doc.querySelector(SELECTORS.plpAbout);
      const aboutEl = document.querySelector(SELECTORS.plpAbout);
      if (newAbout && aboutEl) {
        aboutEl.innerHTML = newAbout.innerHTML;
        aboutEl.hidden = false;
      } else if (!newAbout && aboutEl) {
        aboutEl.hidden = true;
      }
    }

    // Push URL state (filters/sort only - page & availability stay internal)
    if (pushState) {
      const urlParams = new URLSearchParams(STATE.params);
      urlParams.delete('page');
      urlParams.delete(AVAIL_PARAM);
      const searchStr = urlParams.toString();
      const newUrl = searchStr ? `${STATE.baseUrl}?${searchStr}` : STATE.baseUrl;
      window.history.pushState({}, '', newUrl);
    }

    setLoadingState(false);
    STATE.loading = false;

    if (STATE.pendingFilters) {
      STATE.pendingFilters = false;
      handleFilterChange();
      return true;
    }

    if (typeof window.tdcRefreshAnimations === 'function') {
      window.tdcRefreshAnimations();
    }

    // Re-initialize quick-add buttons on newly rendered cards
    if (window.TDC && typeof window.TDC.initProductCards === 'function') {
      window.TDC.initProductCards();
    }

    // Re-establish infinite scroll observation after grid update
    if (STATE.infiniteScroll) {
      observeInfiniteScroll();
    }

    return true;
  }

  function paramValues(name) {
    return STATE.params.getAll(name).map(v => {
      try { return decodeURIComponent(v).replace(/\+/g, ' '); }
      catch (e) { return v; }
    });
  }

  function syncFilterInputs(filterBody) {
    filterBody.querySelectorAll('input[type="checkbox"]').forEach(input => {
      const name = input.name || input.dataset.filterParam;
      const values = paramValues(name);
      const raw = String(input.value);
      let decoded = raw;
      try { decoded = decodeURIComponent(raw); } catch (e) {}
      input.checked = values.includes(raw) || values.includes(decoded);
    });

    filterBody.querySelectorAll('input[type="number"]').forEach(input => {
      const name = input.name || input.dataset.filterParam;
      const stored = STATE.params.get(name);
      if (stored != null) input.value = stored;
    });
  }


  function scrollToToolbar() {
    const toolbar = document.querySelector(SELECTORS.toolbar);
    if (toolbar) {
      const headerHeight = document.querySelector('.header')?.offsetHeight || 88;
      const top = toolbar.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  function setLoadingState(isLoading) {
    const grid = document.querySelector(SELECTORS.grid);
    const loadMore = document.querySelector(SELECTORS.loadMore);

    if (isLoading) {
      grid?.classList.add('is-loading');
      loadMore?.classList.add('is-loading');
      loadMore?.setAttribute('disabled', 'true');
    } else {
      grid?.classList.remove('is-loading');
      loadMore?.classList.remove('is-loading');
      loadMore?.removeAttribute('disabled');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

(() => {
  function init() {
    document.addEventListener('click', event => {
      const button = event.target.closest('.faq-q');
      if (!button) return;

      const item = button.closest('.faq-item');
      const faq = item?.closest('.faq');
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!item || !faq || !panel) return;

      const isOpen = item.classList.contains('is-open');
      faq.querySelectorAll('.faq-item.is-open').forEach(openItem => {
        closeFaqItem(openItem);
      });

      if (!isOpen) {
        item.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        panel.setAttribute('aria-hidden', 'false');
      }
    });
  }

  function closeFaqItem(item) {
    item.classList.remove('is-open');
    const button = item.querySelector('.faq-q');
    button?.setAttribute('aria-expanded', 'false');
    const panel = document.getElementById(button?.getAttribute('aria-controls'));
    panel?.setAttribute('aria-hidden', 'true');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();